// =============================================================================
// FORM TEMPLATES PANEL - Template selection component
// =============================================================================

import {
  Mail,
  Briefcase,
  Star,
  Calendar,
  Heart,
  FileText,
  X,
  Check,
  type LucideIcon,
} from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Badge } from '../../../components/ui/badge';
import { cn } from '../../../lib/utils';
import { FORM_TEMPLATES, TEMPLATE_CATEGORIES, type FormTemplate } from '../templates';
import { useFormBuilderStore } from '../store';

const ICON_MAP: Record<string, LucideIcon> = {
  Mail,
  Briefcase,
  Star,
  Calendar,
  Heart,
  FileText,
};

interface FormTemplatesPanelProps {
  onClose: () => void;
}

export function FormTemplatesPanel({ onClose }: FormTemplatesPanelProps) {
  const { importForm, setForm } = useFormBuilderStore();

  const handleSelectTemplate = (template: FormTemplate) => {
    // Create a new copy of the form with new IDs
    const formCopy = JSON.parse(JSON.stringify(template.form));
    formCopy.id = crypto.randomUUID();
    formCopy.createdAt = new Date().toISOString();
    formCopy.updatedAt = new Date().toISOString();

    // Regenerate all section and field IDs
    formCopy.sections = formCopy.sections.map((section: { id: string; fields: { id: string }[] }) => ({
      ...section,
      id: crypto.randomUUID(),
      fields: section.fields.map((field: { id: string }) => ({
        ...field,
        id: crypto.randomUUID(),
      })),
    }));

    importForm(formCopy);
    setForm(formCopy);
    onClose();
  };

  // Group templates by category
  const templatesByCategory = FORM_TEMPLATES.reduce<Record<string, FormTemplate[]>>(
    (acc, template) => {
      if (!acc[template.category]) {
        acc[template.category] = [];
      }
      acc[template.category]?.push(template);
      return acc;
    },
    {}
  );

  const categoryOrder = ['contact', 'business', 'hr', 'survey', 'event', 'healthcare'];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
      <div className="bg-white dark:bg-secondary-900 rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-secondary-200 dark:border-secondary-700">
          <div>
            <h2 className="text-xl font-bold text-secondary-900 dark:text-white">
              Modèles de formulaires
            </h2>
            <p className="text-sm text-secondary-500 mt-1">
              Choisissez un modèle pour démarrer rapidement
            </p>
          </div>
          <Button variant="ghost" size="icon" onClick={onClose}>
            <X className="h-5 w-5" />
          </Button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {categoryOrder.map((category) => {
            const templates = templatesByCategory[category];
            if (!templates || templates.length === 0) return null;

            const categoryInfo = TEMPLATE_CATEGORIES[category as keyof typeof TEMPLATE_CATEGORIES];

            return (
              <div key={category} className="mb-8 last:mb-0">
                <h3 className="text-sm font-semibold text-secondary-600 dark:text-secondary-400 uppercase tracking-wider mb-4">
                  {categoryInfo?.labelFr || category}
                </h3>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {templates.map((template) => {
                    const Icon = ICON_MAP[template.icon] || FileText;
                    const fieldCount = template.form.sections.reduce(
                      (acc, s) => acc + s.fields.length,
                      0
                    );

                    return (
                      <button
                        key={template.id}
                        type="button"
                        onClick={() => handleSelectTemplate(template)}
                        className={cn(
                          'group text-left p-5 rounded-xl border-2 transition-all',
                          'border-secondary-200 hover:border-primary-400 hover:shadow-lg',
                          'dark:border-secondary-700 dark:hover:border-primary-500',
                          'bg-white dark:bg-secondary-800'
                        )}
                      >
                        <div className="flex items-start gap-4">
                          <div
                            className="flex items-center justify-center w-12 h-12 rounded-xl"
                            style={{ backgroundColor: `${template.form.theme.primaryColor}15` }}
                          >
                            <Icon
                              className="h-6 w-6"
                              style={{ color: template.form.theme.primaryColor }}
                            />
                          </div>

                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                              <h4 className="font-semibold text-secondary-900 dark:text-white">
                                {template.name}
                              </h4>
                              <Badge variant="secondary" className="text-[10px]">
                                {fieldCount} champs
                              </Badge>
                            </div>
                            <p className="text-sm text-secondary-500 dark:text-secondary-400 line-clamp-2">
                              {template.description}
                            </p>

                            <div className="flex items-center gap-2 mt-3">
                              <Badge
                                variant="outline"
                                className="text-[10px]"
                                style={{
                                  borderColor: template.form.theme.primaryColor,
                                  color: template.form.theme.primaryColor,
                                }}
                              >
                                {template.form.sections.length} section
                                {template.form.sections.length > 1 ? 's' : ''}
                              </Badge>
                              <span className="text-xs text-secondary-400">
                                {template.form.settings.layout === 'card' && 'Style carte'}
                                {template.form.settings.layout === 'bordered' && 'Style bordures'}
                                {template.form.settings.layout === 'default' && 'Style simple'}
                                {template.form.settings.layout === 'minimal' && 'Style minimal'}
                              </span>
                            </div>
                          </div>

                          <div className="opacity-0 group-hover:opacity-100 transition-opacity">
                            <div className="flex items-center justify-center w-8 h-8 rounded-full bg-primary-500 text-white">
                              <Check className="h-4 w-4" />
                            </div>
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-secondary-200 dark:border-secondary-700 bg-secondary-50 dark:bg-secondary-800/50">
          <div className="flex items-center justify-between">
            <p className="text-sm text-secondary-500">
              {FORM_TEMPLATES.length} modèles disponibles
            </p>
            <Button variant="outline" onClick={onClose}>
              Créer un formulaire vide
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
